import React from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell } from '@/components/atoms';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { MARKET_PROPS } from '@/constant/market/market.constant';
import { tid } from '@/utils/testId';
import { MARKET_TEST_IDS as TID } from '@/constant/testIds/market.testIds';
import type { IMarketProps } from './markets.interface';

/* ── Column definitions ───────────────────────────────────────────────────── */
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
): ColumnDef<IMarketProps>[] {
  return [
    {
      accessor: 'sourceOperatorId',
      header: MARKET_PROPS.COLUMNS.OPERATOR,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_OPERATOR(`${idx}`))}
          value={item.sourceOperatorId}
        />
      ),
    },
    {
      accessor: 'sourceMarketId',
      header: MARKET_PROPS.COLUMNS.MARKET_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET_ID(`${idx}`))}
          value={item.sourceMarketId}
          tooltip
          variant="primary"
          copy={{ copyKey: `mid-${item.marketId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'name',
      header: MARKET_PROPS.COLUMNS.MARKET_NAME,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_NAME(`${idx}`))}
          value={item.name}
          tooltip
          variant="primary"
          copy={{ copyKey: `mname-${item.marketId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'locationName',
      header: MARKET_PROPS.COLUMNS.LOCATION,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_LOCATION(`${idx}`))}
          value={item.location?.name || item.locationName}
        />
      ),
    },
    {
      accessor: 'deletedOn',
      header: MARKET_PROPS.COLUMNS.STATUS,
      render: (item, idx) => (
        <Tooltip
          content={
            item.deletedOn
              ? `Deleted: ${getDateTime(item.deletedOn)}`
              : 'Active'
          }
          placement="right"
        >
          <div>
          <PillCell
            testId={tid(TID.COL_STATUS(`${idx}`))}
            value={item.deletedOn ? 'Inactive' : 'Active'}
            preset={item.deletedOn ? 'inactive' : 'active'}
            size="text-[11px]"
            className="border font-semibold cursor-default"
          />
          </div>
        </Tooltip>
      ),
    },
    {
      accessor: 'updatedOn',
      header: MARKET_PROPS.COLUMNS.UPDATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          size="text-[11px]"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: MARKET_PROPS.COLUMNS.CREATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
  ];
}
